import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import type { Demande } from '../../types';
import { formatDateCourt, formatDateLongue } from './format';
import { absenceDe, horaireCourt } from './horaire';

/** 'ok' = proposition enregistrée (désignation ET réponse « oui » en un seul appel) ; 'echec' = rien d'enregistré. */
export type ResultatProposition = 'ok' | 'echec';

/**
 * Une demande qui sort du périmètre (un·e collègue vient de s'y proposer, annulée, séance passée…) répond
 * « Demande introuvable » à qui la voyait à l'instant : on le dit autrement, sans laisser croire à une panne.
 */
export function demandeChangee(err: unknown): boolean {
  const m = err instanceof Error ? err.message : '';
  return m === 'Demande introuvable' || m.startsWith("Cette action n'est plus possible") || m === 'Cette séance est déjà passée.';
}

export function messageErreurDemande(err: unknown): string {
  const m = err instanceof Error ? err.message : '';
  if (m === 'Demande introuvable') {
    return "Cette demande n'est plus disponible : un·e collègue vient peut-être de s'y proposer, ou elle a été annulée. La liste a été mise à jour.";
  }
  return m || "Votre proposition n'a pas pu être enregistrée. Réessayez.";
}

/**
 * « Je me propose » : une fenêtre qui récapitule AVANT d'engager (avant, un seul clic
 * désignait et confirmait sans rien dire), et signale les conflits sans bloquer.
 * Partagée par la liste « Demandes des collègues », le calendrier et la fiche en
 * lecture seule. Se désigner soi-même vaut confirmation : le SERVEUR enregistre la réponse « oui » avec la désignation (docs/DECISIONS.md §38),
 * un seul appel — il n'y a plus de moment où la personne serait désignée sans avoir répondu.
 */
export function ProposerRemplacementModal({ demande, maPersonneId, onClose, onFini }: {
  demande: Demande;
  maPersonneId: number;
  onClose: () => void;
  /** Appelée après la tentative : le parent recharge ses données et, hors 'echec', ferme la fenêtre. */
  onFini: (resultat: ResultatProposition) => void;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // La demande a changé entre-temps : on n'invite pas à retenter une proposition qui ne peut plus aboutir.
  const [perimee, setPerimee] = useState(false);

  useEffect(() => {
    const f = (e: KeyboardEvent) => { if (e.key === 'Escape' && !enCours) onClose(); };
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
  }, [enCours, onClose]);

  async function valider() {
    setEnCours(true);
    setErreur(null);
    try {
      await api.appliquerAction(demande.id, 'saisir_remplacant', { remplacant_personne_id: maPersonneId, stagiaire: false });
      onFini('ok');
    } catch (err) {
      // Deux volontaires en même temps : le serveur n'en retient qu'un·e et dit à l'autre que la séance est déjà pourvue (demande changée).
      setErreur(messageErreurDemande(err));
      setPerimee(demandeChangee(err));
      onFini('echec');
    } finally {
      setEnCours(false);
    }
  }

  const horaire = horaireCourt(demande.atelier_heure_debut, demande.atelier_heure_fin);
  const c = demande.conflits;
  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="proposer-titre">
        <div className="modal-header">
          <h3 className="modal-title" id="proposer-titre">Vous proposer pour remplacer {demande.absent_nom ?? 'un·e collègue'} ?</h3>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Fermer" disabled={enCours}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--bg-inset)', borderRadius: 'var(--r1)', padding: '10px 12px', marginBottom: 12 }}>
            <div className="font-medium">{demande.atelier_nom}{demande.pole_nom ? ` · ${demande.pole_nom}` : ''}</div>
            <div className="text-sm text-ink-2">{formatDateLongue(demande.date_atelier)}</div>
            <div className="text-sm text-ink-2">{[horaire, demande.atelier_lieu, absenceDe(demande.absent_nom)].filter(Boolean).join(' · ')}</div>
          </div>
          {c?.anime && (
            <div className="proposer-alerte">⚠ Vous animez « {c.anime} » à cette heure. Vérifiez que vous pouvez vous libérer.</div>
          )}
          {c?.absente && (
            <div className="proposer-alerte">⚠ Vous êtes absent·e ce jour-là.</div>
          )}
          <p className="text-sm text-ink-2">
            En vous proposant, vous confirmez votre disponibilité. L'équipe gestion validera ensuite, et vous serez prévenu·e.
          </p>
          {erreur && <p className="text-sm" style={{ color: 'var(--err)', marginTop: 12 }}>{erreur}</p>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" onClick={onClose} disabled={enCours}>Retour</button>
          <button type="button" className="btn btn-accent" onClick={valider} disabled={enCours || perimee}>
            {enCours ? <span className="spinner spinner-sm" /> : 'Oui, je me propose'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Confirmation affichée À LA PLACE de la demande où la personne vient de cliquer : dès
 * la confirmation, la demande sort du périmètre « collègues » (elle n'est plus en
 * recherche) et disparaîtrait sinon sans un mot d'explication.
 */
export function PropositionEnregistree({ demande, onVoir, onFra }: {
  demande: Demande;
  onVoir: () => void;
  onFra?: () => void;
}) {
  const horaire = horaireCourt(demande.atelier_heure_debut, demande.atelier_heure_fin);
  return (
    <div className="proposition-confirmee">
      <div className="proposition-confirmee-titre">
        ✓ Proposition enregistrée — {demande.atelier_nom} · {formatDateCourt(demande.date_atelier)}{horaire ? ` · ${horaire}` : ''}
      </div>
      <div className="proposition-confirmee-sub">En attente de validation par l'équipe gestion. Vous serez prévenu·e de la suite.</div>
      <div className="flex gap-12">
        <button type="button" className="rech-toggle-link" style={{ marginTop: 4 }} onClick={onVoir}>Suivre cette demande</button>
        {onFra && (
          <button type="button" className="rech-toggle-link" style={{ marginTop: 4 }} onClick={onFra}>Préparer avec la FRA</button>
        )}
      </div>
    </div>
  );
}
